import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api/admin';

/**
 * Cookie name for CSRF token (matches backend cookies.py)
 * This cookie is readable by JavaScript (not httpOnly)
 */
const CSRF_COOKIE_NAME = 'gus_csrf_token';

/**
 * Get CSRF token from cookie.
 * The CSRF token is set by the backend in a readable cookie.
 */
function getCsrfToken(): string | null {
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === CSRF_COOKIE_NAME) {
      return value;
    }
  }
  return null;
}

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // CRITICAL: Include cookies in requests for httpOnly token auth
  // This is required for cookie-based authentication (SOC 2 CC6.1)
  withCredentials: true,
});

// Add CSRF token to state-changing requests
// CSRF protection is required when using cookie-based auth
api.interceptors.request.use(
  (config) => {
    // Add CSRF token for state-changing methods
    const method = config.method?.toUpperCase();
    if (method === 'POST' || method === 'PUT' || method === 'DELETE' || method === 'PATCH') {
      const csrfToken = getCsrfToken();
      if (csrfToken) {
        config.headers['X-CSRF-Token'] = csrfToken;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear user data from localStorage (token is in httpOnly cookie, cleared by server)
      localStorage.removeItem('adminUser');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export interface LoginCredentials {
  username: string;
  password: string;
}

export interface Tenant {
  id: string;
  tenant_id: string;
  organization_name: string;
  subdomain: string;
  city_name: string;
  city_id: string;
  city_center_lat: number;
  city_center_lng: number;
  resource_tier: string;
  max_users: number;
  max_storage_gb: number;
  max_api_calls_per_day: number;
  status: string;
  is_active: boolean;
  admin_email: string;
  created_at: string;
  deployment_count?: number;
  last_deployed_at?: string;
}

export interface UsageMetric {
  id: string;
  tenant_id: string;
  metric_date: string;
  api_calls: number;
  unique_users: number;
  storage_used_gb: number;
  datasets_created: number;
  maps_saved: number;
  documents_uploaded: number;
  ai_queries: number;
  avg_response_time_ms?: number;
  error_rate?: number;
  uptime_percentage: number;
}

export interface Dataset {
  id: string;
  tenant_id: string;
  project_id: string;
  name: string;
  description?: string;
  file_type: string;
  data_category: string;
  file_size_mb: number;
  status: string;
  record_count?: number;
  feature_count?: number;
  column_count?: number;
  is_validated: boolean;
  uploaded_at: string;
  uploaded_by: string;
  detected_schema?: any;
  validated_schema?: any;
  validation_errors?: string[];
}

export interface DatasetUploadResult {
  status: string;
  dataset_id?: string;
  file_path?: string;
  file_size_mb?: number;
  validation_result?: {
    is_valid: boolean;
    detected_type: string;
    detected_category: string;
    detected_schema: any;
    validation_errors: string[];
    warnings: string[];
  };
}

export interface SystemHealthData {
  timestamp: string;
  cpu_usage_percent: number;
  memory_usage_percent: number;
  disk_usage_percent: number;
  postgis_healthy: boolean;
  redis_healthy: boolean;
  nginx_healthy: boolean;
  active_containers: number;
  active_alerts: any[];
}

// Auth API
export const authAPI = {
  login: async (credentials: LoginCredentials) => {
    const response = await api.post('/auth/login', credentials);
    // Note: Token is now set in httpOnly cookie by the backend
    // Response contains user info and csrf_token
    return response.data;
  },

  // Firebase Authentication - sends Firebase ID token to backend
  firebaseLogin: async (idToken: string) => {
    const response = await api.post('/auth/firebase', { id_token: idToken });
    // Note: Token is now set in httpOnly cookie by the backend
    return response.data;
  },

  // Legacy Google OAuth (deprecated - use firebaseLogin instead)
  googleLogin: async (credential: string) => {
    const response = await api.post('/auth/google', { credential });
    // Note: Token is now set in httpOnly cookie by the backend
    return response.data;
  },

  /**
   * Logout - clears httpOnly auth cookies server-side
   * This endpoint clears the httpOnly cookies that JavaScript cannot access.
   */
  logout: async (): Promise<void> => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      // Even if the server call fails, clear local state
      console.error('Logout API call failed:', error);
    }
    // Clear user info from localStorage
    localStorage.removeItem('adminUser');
  },
};

// Tenant API
export const tenantAPI = {
  list: async () => {
    const response = await api.get<{ tenants: Tenant[] }>('/tenants');
    return response.data.tenants;
  },
  
  get: async (tenantId: string) => {
    const response = await api.get<Tenant>(`/tenants/${tenantId}`);
    return response.data;
  },
  
  create: async (tenant: Partial<Tenant>) => {
    const response = await api.post('/tenants', tenant);
    return response.data;
  },
  
  update: async (tenantId: string, updates: Partial<Tenant>) => {
    const response = await api.put(`/tenants/${tenantId}`, updates);
    return response.data;
  },
  
  suspend: async (tenantId: string) => {
    const response = await api.post(`/tenants/${tenantId}/suspend`);
    return response.data;
  },
  
  activate: async (tenantId: string) => {
    const response = await api.post(`/tenants/${tenantId}/activate`);
    return response.data;
  },
};

// Activity Feed types
export interface LoginActivity {
  timestamp: string;
  ip_address: string;
  status: 'success' | 'failed';
  response_code: number;
  path: string;
  user_agent: string;
  username: string;
  email: string;
}

export interface ErrorActivity {
  timestamp: string;
  action: string;
  path: string;
  method: string;
  status_code: number;
  error_type: 'client_error' | 'server_error';
  ip_address: string;
  duration_ms?: number;
  username: string;
}

// Metrics API
export const metricsAPI = {
  getOverview: async () => {
    const response = await api.get('/metrics/overview');
    return response.data;
  },

  getLoginActivity: async (limit: number = 20) => {
    const response = await api.get<{
      logins: LoginActivity[];
      total: number;
      fetched_at: string;
    }>(`/metrics/activity/logins?limit=${limit}`);
    return response.data;
  },

  getErrorActivity: async (limit: number = 50) => {
    const response = await api.get<{
      errors: ErrorActivity[];
      total: number;
      top_error_endpoints: { endpoint: string; count: number }[];
      fetched_at: string;
    }>(`/metrics/activity/errors?limit=${limit}`);
    return response.data;
  },
};

// Health API
export const healthAPI = {
  check: async () => {
    const response = await api.get('/health');
    return response.data;
  },
};

// Dataset API
export const datasetAPI = {
  upload: async (
    file: File,
    name: string,
    tenantId?: string,
    projectId?: string,
    description?: string,
    dataCategory?: string,
    expectedFileType?: string
  ): Promise<DatasetUploadResult> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', name);
    formData.append('filename', file.name);
    if (tenantId) formData.append('tenant_id', tenantId);
    if (projectId) formData.append('project_id', projectId);
    if (description) formData.append('description', description);
    if (dataCategory) formData.append('data_category', dataCategory);
    if (expectedFileType) formData.append('expected_file_type', expectedFileType);

    const response = await api.post<DatasetUploadResult>('/datasets/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  list: async (tenantId?: string, projectId?: string, status?: string) => {
    const params = new URLSearchParams();
    if (tenantId) params.append('tenant_id', tenantId);
    if (projectId) params.append('project_id', projectId);
    if (status) params.append('status', status);

    const response = await api.get<{ datasets: Dataset[]; count: number }>(
      `/datasets?${params.toString()}`
    );
    return response.data;
  },

  get: async (datasetId: string) => {
    const response = await api.get<Dataset>(`/datasets/${datasetId}`);
    return response.data;
  },

  delete: async (datasetId: string) => {
    const response = await api.delete(`/datasets/${datasetId}`);
    return response.data;
  },

  getDownloadUrl: async (datasetId: string, expiryHours: number = 24) => {
    const response = await api.get<{ download_url: string; filename: string; expiry_hours: number }>(
      `/datasets/${datasetId}/download-url?expiry_hours=${expiryHours}`
    );
    return response.data;
  },
};

// User types (tenant users, not AdminUser)
export type UserRole = 'admin' | 'editor' | 'viewer' | 'analyst';

export interface UserProjectAccess {
  project_id: string;
  project_name: string;
  project_slug: string;
  role: string;
  is_default: boolean;
}

export interface User {
  id: string;
  tenant_id: string;
  email: string;
  username?: string;
  full_name?: string;
  role: UserRole;
  department?: string;
  title?: string;
  phone?: string;
  is_active: boolean;
  is_verified: boolean;
  last_login?: string;
  created_at: string;
  updated_at?: string;
  invited_at?: string;
  verified_at?: string;
  permissions?: Record<string, any>;
  project_access?: UserProjectAccess[];  // Project roles (populated for group members)
}

export interface UserCreate {
  tenant_id: string;
  email: string;
  full_name?: string;
  username?: string;
  role: UserRole;
  department?: string;
  title?: string;
  phone?: string;
  password?: string;  // If set, user can login immediately without password reset
  send_invite_email?: boolean;
}

export interface UserUpdate {
  full_name?: string;
  role?: UserRole;
  department?: string;
  title?: string;
  phone?: string;
  is_active?: boolean;
}

export interface UserListParams {
  tenant_id?: string;
  search?: string;
  role?: UserRole;
  is_active?: boolean;
  skip?: number;
  limit?: number;
}

// User API
export const userAPI = {
  list: async (params?: UserListParams) => {
    const queryParams = new URLSearchParams();
    if (params?.tenant_id) queryParams.append('tenant_id', params.tenant_id);
    if (params?.search) queryParams.append('search', params.search);
    if (params?.role) queryParams.append('role', params.role);
    if (params?.is_active !== undefined) queryParams.append('is_active', params.is_active.toString());
    if (params?.skip !== undefined) queryParams.append('skip', params.skip.toString());
    if (params?.limit !== undefined) queryParams.append('limit', params.limit.toString());

    const response = await api.get<{ users: User[]; count: number; total: number; skip: number; limit: number }>(
      `/users?${queryParams.toString()}`
    );
    return response.data;
  },

  get: async (userId: string) => {
    const response = await api.get<User>(`/users/${userId}`);
    return response.data;
  },

  create: async (user: UserCreate) => {
    const response = await api.post<{ id: string; email: string; message: string }>(
      '/users',
      user
    );
    return response.data;
  },

  update: async (userId: string, updates: UserUpdate) => {
    const response = await api.put<{ message: string }>(
      `/users/${userId}`,
      updates
    );
    return response.data;
  },

  toggleStatus: async (userId: string) => {
    const response = await api.patch<{ message: string; is_active: boolean }>(
      `/users/${userId}/toggle-status`
    );
    return response.data;
  },

  delete: async (userId: string) => {
    const response = await api.delete<{ message: string }>(
      `/users/${userId}`
    );
    return response.data;
  },
};

// Group types
export interface Permission {
  id: string;
  name: string;
  description: string;
  resource_type: string;
  action: string;
}

export interface Group {
  id: string;
  tenant_id?: string;
  name: string;
  description?: string;
  is_system: boolean;
  created_at: string;
  member_count?: number;
  permissions?: Permission[];
}

export interface GroupCreate {
  name: string;
  description?: string;
  tenant_id?: string;
}

export interface GroupMemberAdd {
  user_id: string;
  group_id: string;
}

export interface PermissionAssignment {
  group_id: string;
  permission_name: string;
  resource_type?: string;
  resource_id?: string;
}

// Feature override types
export interface FeatureOverride {
  id: string;
  group_id: string;
  project_id: string;
  project_name?: string;
  project_slug?: string;
  feature_path: string;
  is_allowed: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface FeatureOverrideCreate {
  project_id: string;
  feature_path: string;
  is_allowed: boolean;
}

// Group API
export const groupAPI = {
  list: async (tenantId?: string) => {
    const params = new URLSearchParams();
    if (tenantId) params.append('tenant_id', tenantId);

    const response = await api.get<{ groups: Group[] }>(
      `/groups?${params.toString()}`
    );
    return response.data;
  },

  create: async (group: GroupCreate) => {
    const response = await api.post<{ id: string; name: string }>(
      '/groups',
      group
    );
    return response.data;
  },

  getMembers: async (groupId: string) => {
    const response = await api.get<{ members: any[] }>(
      `/groups/${groupId}/members`
    );
    return response.data;
  },

  addMember: async (membership: GroupMemberAdd) => {
    const response = await api.post<{ message: string }>(
      '/groups/members',
      membership
    );
    return response.data;
  },

  removeMember: async (groupId: string, userId: string) => {
    const response = await api.delete<{ message: string }>(
      `/groups/${groupId}/members/${userId}`
    );
    return response.data;
  },

  listPermissions: async () => {
    const response = await api.get<{ permissions: Permission[] }>('/permissions');
    return response.data;
  },

  assignPermission: async (assignment: PermissionAssignment) => {
    const response = await api.post<{ message: string }>(
      '/permissions/assign',
      assignment
    );
    return response.data;
  },

  // Feature override methods
  listFeatureOverrides: async (groupId: string, projectId?: string) => {
    const params = new URLSearchParams();
    if (projectId) params.append('project_id', projectId);

    const response = await api.get<{
      group_id: string;
      group_name: string;
      overrides: FeatureOverride[];
      count: number;
    }>(`/groups/${groupId}/feature-overrides?${params.toString()}`);
    return response.data;
  },

  createFeatureOverride: async (groupId: string, override: FeatureOverrideCreate) => {
    const params = new URLSearchParams();
    params.append('project_id', override.project_id);
    params.append('feature_path', override.feature_path);
    params.append('is_allowed', override.is_allowed.toString());

    const response = await api.post<FeatureOverride>(
      `/groups/${groupId}/feature-overrides?${params.toString()}`
    );
    return response.data;
  },

  updateFeatureOverride: async (groupId: string, overrideId: string, isAllowed: boolean) => {
    const params = new URLSearchParams();
    params.append('is_allowed', isAllowed.toString());

    const response = await api.put<FeatureOverride>(
      `/groups/${groupId}/feature-overrides/${overrideId}?${params.toString()}`
    );
    return response.data;
  },

  deleteFeatureOverride: async (groupId: string, overrideId: string) => {
    const response = await api.delete<{ message: string; id: string; feature_path: string }>(
      `/groups/${groupId}/feature-overrides/${overrideId}`
    );
    return response.data;
  },
};

// Project types
export interface Project {
  id: string;
  tenant_id: string;
  user_id: string;
  name: string;
  description?: string;
  slug: string;
  resource_config?: {
    cpu_cores: number;
    ram_gb: number;
    storage_gb: number;
    max_concurrent_users: number;
  };
  model_config?: {
    openrouter_model: string;
    embedding_model: string;
    reranking_model: string;
  };
  social_atlas_config?: any;
  color?: string;
  icon?: string;
  created_at: string;
  updated_at?: string;
  dataset_count?: number;
}

export interface ProjectCreate {
  tenant_id: string;
  name: string;
  description?: string;
  color?: string;
  icon?: string;
}

export interface ProjectUpdate {
  name?: string;
  description?: string;
  color?: string;
  icon?: string;
  resource_config?: {
    cpu_cores: number;
    ram_gb: number;
    storage_gb: number;
    max_concurrent_users: number;
  };
  model_config?: {
    openrouter_model: string;
    embedding_model: string;
    reranking_model: string;
  };
}

// Project API
export interface TenantConfigEntry {
  key: string;
  value: string | null;
  category: string | null;
  updated_at: string | null;
}

/** Mirrors TenantConfigResponse in backend/app/api/routes/tenant_config.py. */
export interface TenantConfigResponse {
  project_slug: string;
  reachable: boolean;
  readable: boolean;
  error: string | null;
  error_kind: string | null;
  database: { host: string; name: string } | null;
  /** Set only when the project's database_url names a different database from the one read. */
  recorded_database: { host: string; name: string } | null;
  credential: string | null;
  shared_with: string[];
  sharing_unknown: string[];
  slug_state: 'match' | 'mismatch' | 'subdomain' | 'empty' | 'absent' | 'unknown';
  slug_value: string | null;
  /** Categories holding a PROJECT_SLUG row, in the order GUS resolves them; the last wins. */
  slug_categories: string[];
  config: TenantConfigEntry[];
  /** Words and limits GUS replaces when unusable; empty when nothing could be read. */
  checks: TenantValueCheck[];
}

/** Mirrors TenantValueCheck in backend/app/api/routes/tenant_config.py. */
export interface TenantValueCheck {
  key: string;
  state: 'ok' | 'unset' | 'invalid' | 'not_read';
  value: string | null;
  message: string;
  message_ar: string;
}

/**
 * `string_map` is a flat object of string keys to string values, edited as
 * key/value rows (e.g. Satisfaction's `config.topic_icons.icons` and
 * `config.topic_canonical`). Any other type a newer GUS publishes is shown
 * read-only by FlagLeafControl rather than guessed at.
 */
export type FlagLeafType = 'boolean' | 'integer' | 'string' | 'string[]' | 'string_map';

export interface FlagLeaf {
  key: string;
  type: FlagLeafType;
  default: boolean | number | string | string[] | Record<string, string> | null;
  nullable?: true;
  role?: 'caption' | 'caption_ar' | 'rbac';
  mutable?: false;
  immutable_reason?: string;
}

export interface FlagAlias {
  path: string;
  deprecated: true;
}

export interface FlagNode {
  key: string;
  path: string;
  kind: 'module' | 'tab' | 'subtab' | 'section' | 'tool' | 'submenu' | 'utility';
  container?: string;
  label: string;
  label_ar: string | null;
  aliases?: FlagAlias[];
  gated_by?: string;
  gated_note?: string;
  leaves: FlagLeaf[];
  children?: FlagNode[];
}

export interface FlagManifest {
  manifest_version: 1;
  gus_version: string;
  generated_at: string;
  alias_rule: 'first_key_present';
  roles_vocabulary: string[];
  containers: string[];
  modules: FlagNode[];
}

/** Mirrors the payload built in backend/app/api/routes/flag_manifest.py. */
export interface FlagManifestResponse {
  manifest: FlagManifest | null;
  source: 'live' | 'cache' | 'none';
  status:
    | 'ok'
    | 'not_provisioned'
    | 'unauthorized'
    | 'unreachable'
    | 'unsupported'
    | 'no_deployment_url'
    | 'portal_key_missing';
  detail: string | null;
  gus_version: string | null;
  fetched_at: string | null;
}

/** The levels and measures a tenant's GUS can read (GUS `GET /api/atlas-v2/executive/catalogue`). */
export interface ExecutiveCatalogue {
  shape: number;
  version: string;
  levels: Array<{ code: string; label: { en: string | null; ar: string | null } }>;
  coarsest: string | null;
  dashboards: string[];
  groups: ExecutiveCatalogueGroup[];
  domain_off: string[];
  partial: boolean;
}

export interface ExecutiveCatalogueGroup {
  id: string;
  label: { en: string | null; ar: string | null };
  dashboard: string;
  measures: ExecutiveMeasure[];
}

export interface ExecutiveMeasure {
  id: string;
  label: { en: string | null; ar: string | null };
  group: string;
  dashboard: string;
  kind: 'number' | 'category';
  available: boolean;
  reason_key: string | null;
  city: { ok: boolean; reason_key: string | null };
  rank_levels: string[];
  city_relative: boolean;
}

/** GUS's latest readings run (`GET /api/atlas-v2/executive/admin/readings/status`). */
export interface ExecutiveReadingsRun {
  run_id: number;
  period: string;
  status: string;
  finished_at: string | null;
  code_version?: number;
  counts?: { measures: number; failed: string[] } | null;
}

/** Mirrors ExecutiveAnswer in backend/app/api/routes/executive_admin.py. */
export interface ExecutiveAnswer<T> {
  outcome:
    | 'ok' | 'started' | 'already_running' | 'not_built' | 'rate_limited' | 'failed' | 'not_signed_in'
    | 'refused' | 'not_admin' | 'no_deployment_url' | 'not_allowed' | 'unreachable' | 'not_deployed'
    | 'unexpected'
    // Browser side only: this browser could not produce the admin's GUS sign-in, so nothing was sent.
    | 'token_error';
  http_status: number | null;
  body: T | null;
  detail: string | null;
  message: string;
  message_ar: string;
}

/**
 * The admin's GUS sign-in: the Firebase ID token the portal and GUS share. The
 * GUS routes behind the Leadership Brief tools check it themselves. Null only
 * when nobody is signed in to Firebase in this browser (a session that did not
 * come from a Firebase sign-in); a failure to load Firebase or refresh the
 * token throws, so it is never mistaken for "not signed in". Imported lazily so
 * that loading this module never starts Firebase.
 */
export async function gusIdToken(): Promise<string | null> {
  const { auth } = await import('../firebase');
  await auth.authStateReady();
  return auth.currentUser ? auth.currentUser.getIdToken() : null;
}

/** What the screen shows when this browser could not produce the sign-in at all. */
const tokenError = <T,>(e: any): ExecutiveAnswer<T> => {
  console.error('Leadership Brief: could not get the GUS sign-in token', e);
  return {
    outcome: 'token_error',
    http_status: null,
    body: null,
    detail: e?.code || e?.name || null,
    message: 'This browser could not get your GUS sign-in (a network or Firebase problem, not your access). '
      + 'Reload the page and try again.',
    message_ar: 'تعذّر على هذا المتصفح الحصول على تسجيل دخولك إلى GUS (مشكلة في الشبكة أو في Firebase، لا في صلاحياتك). '
      + 'أعد تحميل الصفحة وحاول مجددًا.',
  };
};

/** One call to a Leadership Brief route, carrying the sign-in when there is one. */
async function asAdmin<T>(send: (headers: Record<string, string>) => Promise<{ data: ExecutiveAnswer<T> }>) {
  let token: string | null;
  try {
    token = await gusIdToken();
  } catch (e) {
    return tokenError<T>(e);
  }
  const response = await send(token ? { 'X-GUS-Id-Token': token } : {});
  return response.data;
}

/** The Leadership Brief tools, run on the project's GUS as the signed-in admin. */
export const executiveAPI = {
  catalogue: (projectId: string) =>
    asAdmin<ExecutiveCatalogue>((headers) =>
      api.get(`/projects/${projectId}/executive/catalogue`, { headers })),

  readingsStatus: (projectId: string) =>
    asAdmin<{ last_run: ExecutiveReadingsRun | null }>((headers) =>
      api.get(`/projects/${projectId}/executive/readings/status`, { headers })),

  startReadings: (projectId: string) =>
    asAdmin<null>((headers) =>
      api.post(`/projects/${projectId}/executive/readings`, null, { headers })),
};

export const projectAPI = {
  list: async (tenantId?: string) => {
    const params = new URLSearchParams();
    if (tenantId) params.append('tenant_id', tenantId);

    const response = await api.get<{ projects: Project[]; count: number }>(
      `/projects?${params.toString()}`
    );
    return response.data;
  },

  get: async (projectId: string) => {
    const response = await api.get<Project>(`/projects/${projectId}`);
    return response.data;
  },

  getTenantConfig: async (projectId: string) => {
    const response = await api.get<TenantConfigResponse>(
      `/projects/${projectId}/tenant-config`
    );
    return response.data;
  },

  getFlagManifest: async (projectId: string) => {
    const response = await api.get<FlagManifestResponse>(
      `/projects/${projectId}/flag-manifest`
    );
    return response.data;
  },

  create: async (project: ProjectCreate) => {
    const response = await api.post<{ id: string; slug: string; message: string }>(
      '/projects',
      project
    );
    return response.data;
  },

  update: async (projectId: string, updates: ProjectUpdate) => {
    const response = await api.put<{ message: string }>(
      `/projects/${projectId}`,
      updates
    );
    return response.data;
  },

  delete: async (projectId: string) => {
    const response = await api.delete<{ message: string }>(
      `/projects/${projectId}`
    );
    return response.data;
  },

  // Project User Access Management
  listUsers: async (projectId: string) => {
    const response = await api.get<{
      project_id: string;
      project_name: string;
      users: ProjectUser[];
      count: number;
    }>(`/projects/${projectId}/users`);
    return response.data;
  },

  addUser: async (projectId: string, data: ProjectUserAdd) => {
    const response = await api.post<{
      access_id: string;
      user_id: string;
      email: string;
      project_id: string;
      role: string;
      message: string;
    }>(`/projects/${projectId}/users`, data);
    return response.data;
  },

  updateUserRole: async (projectId: string, userId: string, role: string) => {
    const response = await api.put<{
      user_id: string;
      email: string;
      project_id: string;
      old_role: string;
      new_role: string;
      message: string;
    }>(`/projects/${projectId}/users/${userId}/role`, { role });
    return response.data;
  },

  removeUser: async (projectId: string, userId: string) => {
    const response = await api.delete<{
      user_id: string;
      email: string;
      project_id: string;
      message: string;
    }>(`/projects/${projectId}/users/${userId}`);
    return response.data;
  },
};

// Project User Access types
export interface ProjectUser {
  access_id: string;
  user_id: string;
  email: string;
  full_name?: string;
  role: string;
  is_active: boolean;
  is_default_project: boolean;
  granted_at: string;
  granted_by?: {
    name: string;
    email: string;
  };
  last_accessed_at?: string;
}

export interface ProjectUserAdd {
  email: string;
  role: string;
  is_default_project?: boolean;
}

// My Projects API (for current user)
export const myProjectsAPI = {
  list: async () => {
    const response = await api.get<{
      tenants: {
        tenant_id: string;
        tenant_name: string;
        projects: {
          project_id: string;
          project_slug: string;
          project_name: string;
          role: string;
          status: string;
          url: string;
          last_accessed_at?: string;
        }[];
      }[];
      last_accessed?: string;
      total_projects: number;
    }>('/users/me/projects');
    return response.data;
  },

  recordAccess: async (projectId: string) => {
    const response = await api.post<{
      status: string;
      message: string;
      project_id: string;
      project_slug: string;
    }>(`/users/me/projects/${projectId}/access`);
    return response.data;
  },
};

// Audit Log types
export interface AuditLog {
  id: string;
  user_id: string;
  username: string;
  action: string;
  action_category?: string;
  request_method?: string;
  request_path?: string;
  request_body?: any;
  response_status?: number;
  response_body?: any;
  duration_ms?: number;
  ip_address?: string;
  user_agent?: string;
  details?: Record<string, any>;
  metadata?: Record<string, any>;
  timestamp: string;
}

export interface AuditLogQueryParams {
  user_id?: string;
  action_category?: string;
  action?: string;
  status_code_min?: number;
  status_code_max?: number;
  date_from?: string;  // ISO datetime
  date_to?: string;    // ISO datetime
  limit?: number;
  offset?: number;
}

// Portal Admin types
export interface PortalAdmin {
  id: string;
  username: string;
  email: string;
  full_name?: string;
  is_super_admin: boolean;
  can_create_tenants: boolean;
  can_delete_tenants: boolean;
  can_modify_resources: boolean;
  can_view_all_metrics: boolean;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
  last_login?: string;
}

export interface PortalAdminCreate {
  username: string;
  email: string;
  full_name?: string;
  password?: string;
  is_super_admin?: boolean;
  can_create_tenants?: boolean;
  can_delete_tenants?: boolean;
  can_modify_resources?: boolean;
  can_view_all_metrics?: boolean;
}

export interface PortalAdminUpdate {
  username?: string;
  email?: string;
  full_name?: string;
  password?: string;
  is_super_admin?: boolean;
  can_create_tenants?: boolean;
  can_delete_tenants?: boolean;
  can_modify_resources?: boolean;
  can_view_all_metrics?: boolean;
  is_active?: boolean;
}

// Portal Admin API
export const portalAdminAPI = {
  list: async (params?: { search?: string; is_active?: boolean }) => {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append('search', params.search);
    if (params?.is_active !== undefined) queryParams.append('is_active', params.is_active.toString());

    const response = await api.get<{ users: PortalAdmin[]; count: number; total: number }>(
      `/portal-users?${queryParams.toString()}`
    );
    return response.data;
  },

  get: async (userId: string) => {
    const response = await api.get<PortalAdmin>(`/portal-users/${userId}`);
    return response.data;
  },

  create: async (admin: PortalAdminCreate) => {
    const response = await api.post<PortalAdmin & { temporary_password?: string; message: string }>(
      '/portal-users',
      admin
    );
    return response.data;
  },

  update: async (userId: string, updates: PortalAdminUpdate) => {
    const response = await api.put<PortalAdmin & { message: string }>(
      `/portal-users/${userId}`,
      updates
    );
    return response.data;
  },

  delete: async (userId: string) => {
    const response = await api.delete<{ message: string; id: string; username: string }>(
      `/portal-users/${userId}`
    );
    return response.data;
  },
};

// Audit Log API
export const auditAPI = {
  query: async (params: AuditLogQueryParams) => {
    const response = await api.post<{
      logs: AuditLog[];
      total_count: number;
      page: number;
      page_size: number;
      has_more: boolean;
    }>('/audit-logs/query', params);
    return response.data;
  },

  get: async (logId: string) => {
    const response = await api.get<AuditLog>(`/audit-logs/${logId}`);
    return response.data;
  },
};

// Service Status Types
export interface ServiceStatus {
  name: string;
  configured: boolean;
  connected: boolean;
  required: boolean;
  message: string;
}

export interface ServiceStatusResponse {
  timestamp: string;
  overall_status: 'fully_configured' | 'operational' | 'partial' | 'not_configured';
  configured_count: number;
  total_count: number;
  required_configured: number;
  required_total: number;
  services: {
    database: ServiceStatus;
    redis: ServiceStatus;
    kubernetes: ServiceStatus;
    pinecone: ServiceStatus;
    azure_storage: ServiceStatus;
    firebase: ServiceStatus;
    openrouter: ServiceStatus;
    email: ServiceStatus;
  };
}

// System Status API
export const systemAPI = {
  getServiceStatus: async () => {
    const response = await api.get<ServiceStatusResponse>('/system/services');
    return response.data;
  },

  getHealth: async () => {
    const response = await api.get<{
      status: string;
      timestamp: string;
      components: Record<string, string>;
    }>('/system/health');
    return response.data;
  },
};

export default api;
// GUS Weekly recipients API (who receives the Thursday email)
export interface GusWeeklyRecipient {
  id: string;
  email: string;
  name: string | null;
  active: boolean;
  linked: 'portal_admin' | 'tenant_user' | null;
  project_name: string | null;
  added_by: string | null;
  created_at: string;
}

export interface GusWeeklyRecipientList {
  recipients: GusWeeklyRecipient[];
  active_count: number;
  max_active: number;
}

export interface GusWeeklyRecipientCreate {
  email: string;
  name?: string;
  admin_user_id?: string;
  user_id?: string;
}

export interface GusWeeklyRecipientUpdate {
  name?: string | null;
  active?: boolean;
}

export interface GusWeeklySuggestion {
  email: string;
  name: string | null;
  kind: 'portal_admin' | 'tenant_user';
  admin_user_id?: string | null;
  user_id?: string | null;
  project_name?: string | null;
}

export interface GusWeeklyStatus {
  run_date: string;
  status: 'sending' | 'sent' | 'failed';
  help_hub_url: string | null;
}

export const gusWeeklyAPI = {
  list: async () => {
    const response = await api.get<GusWeeklyRecipientList>('/gus-weekly/recipients');
    return response.data;
  },

  add: async (body: GusWeeklyRecipientCreate) => {
    const response = await api.post<GusWeeklyRecipient>('/gus-weekly/recipients', body);
    return response.data;
  },

  update: async (id: string, body: GusWeeklyRecipientUpdate) => {
    const response = await api.patch<GusWeeklyRecipient>(`/gus-weekly/recipients/${id}`, body);
    return response.data;
  },

  remove: async (id: string) => {
    await api.delete(`/gus-weekly/recipients/${id}`);
  },

  suggest: async (q: string) => {
    const response = await api.get<{ suggestions: GusWeeklySuggestion[] }>(
      '/gus-weekly/recipients/suggest',
      { params: { q } }
    );
    return response.data.suggestions;
  },

  status: async () => {
    const response = await api.get<GusWeeklyStatus | null>('/gus-weekly/status');
    return response.data;
  },
};
